/**
 * Camel Up rule constants.
 *
 * Values reflect first-edition rules with the planning-doc-confirmed
 * 5 / 3 / 2 / 2 leg-bet ticket ladder. If a value changes, update
 * `plans/camel-up.md` so it stays in sync.
 */

import type { LegBetTicket, RacingColor } from '../types'

// --- Board / camels --------------------------------------------------------

/** Track length. Space 16 is the last space; crossing past it = finish. */
export const BOARD_SIZE = 16

/** Five racing camels, listed in canonical UI order. */
export const RACING_COLORS = ['red', 'yellow', 'green', 'blue', 'purple'] as const

/** Two crazy camels. Move backward; cannot win or be bet on. */
export const CRAZY_COLORS = ['black', 'white'] as const

// --- Pyramid / dice --------------------------------------------------------

/**
 * The dice that go into the pyramid each leg: one per racing color + one
 * shared grey die for both crazy camels. A leg ends as soon as 5 of the 6
 * have been drawn (the last die is left undrawn and discarded).
 */
export const PYRAMID_DICE = [
  'red',
  'yellow',
  'green',
  'blue',
  'purple',
  'grey',
] as const

export const DIE_MIN_VALUE = 1
export const DIE_MAX_VALUE = 3

// --- Bets ------------------------------------------------------------------

/**
 * Leg-bet ticket stack per color, in TAKE order: the first player to bet
 * that color gets index 0 (the 5), then 3, then 2, then 2.
 */
export function defaultLegBetStack(color: RacingColor): LegBetTicket[] {
  return [
    { color, value: 5 },
    { color, value: 3 },
    { color, value: 2 },
    { color, value: 2 },
  ]
}

/** Payout when a leg-bet camel finishes 2nd, regardless of ticket value. */
export const LEG_BET_SECOND_PAYOUT = 1

/** Payout when a leg-bet camel finishes 3rd-or-worse. */
export const LEG_BET_MISS_PAYOUT = -1

/**
 * Race-bet payout ladder by 0-indexed placement order on the pile when the
 * guess is correct. Any placement beyond index 4 pays 1.
 * Source: Camel Up rulebook (overall winner/loser piles).
 */
export const RACE_BET_LADDER: readonly number[] = [8, 5, 3, 2, 1] as const

/** Payout for an incorrect race-bet card. */
export const RACE_BET_MISS_PAYOUT = -1

// --- Player setup ----------------------------------------------------------

export const STARTING_COINS = 3
export const MIN_PLAYERS = 2
export const MAX_PLAYERS = 8
